/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the invitation page component
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */
import * as React from 'react';

import { AppSettings } from '@/AppSettings';
import ChosenThemeActionContext from "@/contexts/styles/ChosenThemeActionContext";
import ChosenThemeContext from "@/contexts/styles/ChosenThemeContext";
import { faMoon, faSun, faUserCircle } from '@fortawesome/pro-duotone-svg-icons';
import {
    AppBar,
    Box,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    Menu,
    Toolbar,
    Tooltip,
    Typography,
    styled
} from '@mui/material';
import { MouseEvent, useCallback, useContext, useState } from 'react';


import colorLogo from '@/assets/img/logo.png';

import { HeaderMenuItem } from '@/api/dtos';
import whiteLogo from '@/assets/img/logo-w.png';
import AuthenticatedUserContext from '@/contexts/authentication/AuthenticatedUserContext';
import ToggleFeedbackButton from '../buttons/ToggleFeedbackButton';
import Icon from './Icon/Icon';
import NavigationInstitutionSelector from './NavigationInstitutionSelector';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface HeaderProps {
    headerMenu?: HeaderMenuItem[] | undefined;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


const Logo = styled('img')(({ theme }) => ({
    maxHeight: '64px',
    minHeight: '64px',

    [`${theme.breakpoints.down('md')}`]: {
        // center all the elements
        //marginLeft: 'auto',
    } 
}));

const StyledAppBar = styled(AppBar)(({theme}) => ({
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px;',
    zIndex: 1201,
    maxHeight: '64px',
    minHeight: '64px',
    
    [`${theme.breakpoints.down('md')}`]: {
        maxHeight: 'unset',
        minHeight: '64px',
    } 
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    background: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.primary.main,

    [`${theme.breakpoints.down('md')}`]: {
        // center all the elements
        display: 'flex',
        //justifyContent: 'center',
        
        flexWrap: 'wrap'
    } 

}));

const StyledDivider = styled(Divider)(({ }) => ({
    margin: `0 !important`
}));

// const StyledButton = styled(ButtonBase)(({ theme }) => ({
//     display: 'flex',
//     flexWrap: 'nowrap',
//     borderRadius: 0,
//     textTransform: 'none',
//     fontWeight: 400,
//     fontSize: '1rem',
//     height: '100%',
//     // backgroundImage: `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.main})`,
//     backgroundPosition: 'bottom left',
//     // backgroundSize: `0% ${theme.spacing(0.75)}px`,
//     backgroundRepeat: 'no-repeat',
//     //padding: `${theme.spacing(0, 1)} !important`,
//     padding: `${theme.spacing(0)} !important`,
//     margin: theme.spacing(0),
//     transition: 'background-size 0.15s, background-position 0s 0.15s',
//     color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main,

//     '& >span': {
//         fontSize: '1rem',

//         '& >div': {
//             '& >svg': {
//                 fontSize: '16px !important',
//                 marginRight: theme.spacing(1),
//             },
//             '& >div': {
//                 display: 'inline-flex'
//             }
//         }
//     },

//     '&:hover': {
//         backgroundPosition: 'bottom right',
//         // backgroundSize: `100% ${theme.spacing(0.75)}px`,
//         // backgroundColor: '#fff',
//     }
// }));

// const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
//     display: 'flex',
//     flexWrap: 'nowrap',
//     justifyContent: 'flex-start',
//     alignItems: 'center',
//     padding: theme.spacing(0, 1),
//     gap: theme.spacing(1),
//     height: '100%',
// }));

// const MenuContainer = styled(Box)(({ theme }) => ({
//     flexGrow: 1,
//     justifyContent: 'flex-end !important',
//     display: 'flex',
//     wrap: 'nowrap',
//     height: '64px',
//     paddingRight: theme.spacing(1),
// }));

// const MenuDivider = styled(Box)(({ theme }) => ({
//     borderLeft: `2px solid ${theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main}`,
//     height: '1.25rem',
//     zIndex: 1000,
// }));

// const MenuItemText = styled(Typography)(({ }) => ({
//     fontSize: '16px',
//     fontWeight: 400
// }));

// const NavigationContainer = styled(Box)(({ theme }) => ({
//     display: 'flex',
//     flexWrap: 'nowrap',
//     alignItems: 'center',
//     height: '100%',
//     paddingRight: theme.spacing(1),
// }));

const PermDiv = styled(Box)(({ theme }) => ({
    // paddingLeft: theme.spacing(1),

    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginLeft: 'auto',

    [`${theme.breakpoints.down('md')}`]: {
        // center all the elements
        // marginLeft: 'unset',
    } 
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.mode === 'dark' ?
    undefined :
    theme.palette.primary.main,

    fontSize: '1.1rem',
    fontWeight: 600,
    textDecoration: 'none',
    lineHeight: '1.25rem',
    paddingLeft: theme.spacing(1.5),
    textTransform: 'uppercase',
    textWrap: 'nowrap',

}));

    

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const Header: React.FunctionComponent<HeaderProps> = ({
    // headerMenu
}) => {
    
    const { data: user } = useContext(AuthenticatedUserContext);


    const chosenTheme = useContext(ChosenThemeContext);
    const setChosenTheme = useContext(ChosenThemeActionContext);

    

    const changeTheme = useCallback(() => {
        setChosenTheme(chosenTheme === 'dark' ? 'light' : 'dark');
    }, [setChosenTheme, chosenTheme]);
    
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };


    return (
        <>
            <StyledAppBar
                position="static"
                // i want it to wrap the content
                color='inherit'
                elevation={4}
            >
                <StyledToolbar>

                    <Logo
                        alt="Breast Cancer Trials" 
                        src={ chosenTheme === 'dark' ? whiteLogo : colorLogo }
                    >
                    </Logo>
                    
                    <StyledTypography>
                        Trial Portal
                    </StyledTypography>
                

                    {/* <MenuContainer>
                        <NavigationContainer>
                            <NavigationInstitutionSelector />
                            {
                                headerMenu && headerMenu?.length > 0 && (
                                    <MenuDivider />
                                )
                            }
                        </NavigationContainer>

                        {
                            headerMenu?.map((menuItem, i) => {
                                return (
                                    <React.Fragment key={i}>
                                        <StyledButton
                                            //@ts-ignore
                                            component="a"
                                            href={menuItem?.link}
                                            target={menuItem.linkNewTab === true ? '_blank' : '_self'}
                                        >
                                            <StyledMenuItem>
                                                <Icon icon={{ prefix: menuItem.iconPrefix as any, iconName: menuItem.iconName as any }} size='lg' />
                                                <MenuItemText>{menuItem.name}</MenuItemText>
                                            </StyledMenuItem>
                                        </StyledButton>
                                    </React.Fragment>
                                );
                            })
                        }

                    </MenuContainer> */}
                    
                    {/* { */}
                        {/* headerMenu && headerMenu?.length > 0 && ( */}
                            {/* <MenuDivider /> */}
                        {/* ) */}
                    {/* } */}

                    <PermDiv>

                        <NavigationInstitutionSelector />
                        
                        {
                            !AppSettings.overrideTheme && (
                                <Tooltip 
                                    title={chosenTheme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                                >
                                    <IconButton
                                        aria-label="theme"
                                        color="inherit"
                                        onClick={changeTheme}
                                    >
                                        <Icon icon={chosenTheme === 'dark' ? faMoon : faSun } size='1x' fixedWidth />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                        {
                            AppSettings.sentry.dsn && 
                                AppSettings.sentry.enableReportABug && (
                                    <ToggleFeedbackButton />
                                )
                            }
                        {
                            !!user && (
                                <>
                                    <IconButton
                                        id="btn-auth"
                                        aria-label="account of current user"
                                        aria-controls="menu-auth"
                                        aria-haspopup="true"
                                        onClick={handleMenu}
                                        color="inherit">
                                        <Icon icon={faUserCircle} />
                                    </IconButton>
                                    <Menu
                                        id="menu-auth"
                                        anchorEl={anchorEl}
                                        keepMounted
                                        MenuListProps={{
                                            disablePadding: true
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        open={anchorEl?.id === 'btn-auth'}
                                        onClose={handleClose}
                                    >
                                        <ListItem>{user?.displayName ?? user?.userName}</ListItem>
                                        <StyledDivider />
                                        <ListItemButton component="a" href={AppSettings.forgotPasswordUrl}>
                                            Change Password
                                        </ListItemButton>
                                        <StyledDivider />
                                        <ListItemButton component="a" href="/auth/logout">
                                            Logout
                                        </ListItemButton>
                                    </Menu>
                                    
                                </>
                            )
                        }
                    </PermDiv>
                </StyledToolbar>
            </StyledAppBar>
        </>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default Header;